import React, { useState, useEffect, useCallback } from 'react';
import { toUrl } from '../lib/config';

interface DOFRow {
  id: number;
  dofNo: string;
  tarih: string;
  dofTalepEden: string;
  dofInKonusu: string;
  diger?: string;
  dofSorumlusu: string;
  adi: string;
  kapanisDurumu?: string;
  createdAt: string;
}

const DuzenleyiciOnleyiciFaaliyetYonetim: React.FC = () => {
  const [dofler, setDofler] = useState<DOFRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDOF, setSelectedDOF] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const fetchDOFler = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const url = toUrl('/api/duzenleyici-onleyici-faaliyet');
      const res = await fetch(url);
      if (!res.ok) {
        const errorText = await res.text();
        console.error('Error response:', errorText);
        throw new Error(`DÖF'ler yüklenemedi: ${res.status}`);
      }
      const data = await res.json();
      setDofler(data.data || []);
    } catch (e: any) {
      console.error('Fetch error:', e);
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDOFler();
  }, [fetchDOFler]);

  const openDeleteModal = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteId(null);
    setShowDeleteModal(false);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    
    try {
      setLoading(true);
      setError('');
      const res = await fetch(toUrl(`/api/duzenleyici-onleyici-faaliyet/${deleteId}`), {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error('Silme işlemi başarısız');
      closeDeleteModal();
      await fetchDOFler();
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleShowDetail = async (id: number) => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(toUrl(`/api/duzenleyici-onleyici-faaliyet/${id}`));
      if (!res.ok) throw new Error('DÖF detayı yüklenemedi');
      const data = await res.json();
      setSelectedDOF(data.data);
      setShowDetailModal(true);
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const filteredDOFler = dofler.filter(dof =>
    dof.dofNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dof.dofTalepEden?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dof.dofInKonusu?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dof.dofSorumlusu?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDurumBadge = (durum?: string) => {
    if (!durum) {
      return (
        <span style={{
          padding: '4px 8px',
          borderRadius: 4,
          fontSize: 12,
          fontWeight: 500,
          backgroundColor: '#fef3c7',
          color: '#92400e'
        }}>
          Devam Ediyor
        </span>
      );
    }
    
    const colors = {
      'Başarılı': { bg: '#d1fae5', color: '#065f46' },
      'Başarısız': { bg: '#fee2e2', color: '#991b1b' }
    };
    const style = colors[durum as keyof typeof colors] || { bg: '#f3f4f6', color: '#374151' };
    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 500,
        backgroundColor: style.bg,
        color: style.color
      }}>
        {durum}
      </span>
    );
  };

  return (
    <div className="admin-page" style={{ padding: 20 }}>
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Düzenleyici/Önleyici Faaliyet (DÖF) Yönetimi</h2>
        <div style={{ fontSize: 14, color: '#6b7280' }}>
          Toplam: {dofler.length} DÖF
        </div>
      </div>

      {error && (
        <div style={{ padding: 12, marginBottom: 16, backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: 6 }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <input
          type="text"
          placeholder="DÖF No, talep eden, konu veya sorumlu birime göre ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 15px',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: 14
          }}
        />
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 20 }}>Yükleniyor...</div>}

      {!loading && filteredDOFler.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
          Henüz DÖF kaydı bulunmuyor.
        </div>
      )}

      {!loading && filteredDOFler.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>DÖF No</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Tarih</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Talep Eden</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Konu</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Sorumlu Birim</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Durum</th>
                <th style={{ padding: 12, textAlign: 'center', fontWeight: 600, fontSize: 13 }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredDOFler.map((dof, idx) => (
                <tr key={dof.id} style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                  <td style={{ padding: 12, fontSize: 13, fontWeight: 600, color: '#3b82f6' }}>{dof.dofNo}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>{formatDate(dof.tarih)}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>{dof.dofTalepEden || '-'}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>
                    {dof.dofInKonusu}
                    {dof.diger && <span style={{ color: '#6b7280', fontSize: 12 }}> ({dof.diger})</span>}
                  </td>
                  <td style={{ padding: 12, fontSize: 13 }}>{dof.dofSorumlusu || '-'}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>{getDurumBadge(dof.kapanisDurumu)}</td>
                  <td style={{ padding: 12, textAlign: 'center' }}>
                    <button
                      onClick={() => handleShowDetail(dof.id)}
                      style={{
                        padding: '6px 12px',
                        marginRight: 8,
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      Detay
                    </button>
                    <button
                      onClick={() => openDeleteModal(dof.id)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      Sil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Silme Onay Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={closeDeleteModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: 8,
              padding: 24,
              maxWidth: 400,
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', color: '#991b1b' }}>DÖF Kaydını Sil</h3>
            <p style={{ margin: '0 0 24px 0', color: '#374151' }}>
              Bu DÖF kaydını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={closeDeleteModal}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: 4,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 14
                }}
              >
                İptal
              </button>
              <button
                onClick={confirmDelete}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detay Modal */}
      {showDetailModal && selectedDOF && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20
          }}
          onClick={() => setShowDetailModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: 8,
              maxWidth: 900,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: 24
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0 }}>DÖF Detayı - {selectedDOF.dofNo}</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer'
                }}
              >
                Kapat
              </button>
            </div>

            {/* Temel Bilgiler */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid #e5e7eb' }}>Temel Bilgiler</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <DetailItem label="DÖF No" value={selectedDOF.dofNo} />
                <DetailItem label="Tarih" value={formatDate(selectedDOF.tarih)} />
                <DetailItem label="Talep Eden" value={selectedDOF.dofTalepEden} />
                <DetailItem label="DÖF Konusu" value={selectedDOF.dofInKonusu} />
                {selectedDOF.diger && <DetailItem label="Diğer (Açıklama)" value={selectedDOF.diger} />}
                <DetailItem label="Sorumlu Birim" value={selectedDOF.dofSorumlusu} />
                <DetailItem label="Sorumlu Kişi" value={selectedDOF.adi} />
                <DetailItem label="Durum" value={getDurumBadge(selectedDOF.kapanisDurumu)} />
              </div>
            </div>

            {/* Planlanan Faaliyetler */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid #e5e7eb' }}>Planlanan Düzenleyici/Önleyici Faaliyetler</h4>
              <div style={{ 
                padding: 16, 
                backgroundColor: '#dbeafe', 
                borderRadius: 6, 
                fontSize: 14,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap'
              }}>
                {selectedDOF.planlayanDuzenleyici || '-'}
              </div>
            </div>

            {/* Gerçekleşen Faaliyetler */}
            {selectedDOF.gerceklesenFaaliyetler && (
              <div style={{ marginBottom: 20 }}>
                <h4 style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid #e5e7eb' }}>Gerçekleşen Faaliyetler</h4>
                <div style={{ 
                  padding: 16, 
                  backgroundColor: '#d1fae5', 
                  borderRadius: 6, 
                  fontSize: 14,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedDOF.gerceklesenFaaliyetler}
                </div>
              </div>
            )}

            {/* Kapanış Bilgileri */}
            {selectedDOF.kapanisDurumu && (
              <div style={{ marginBottom: 20 }}>
                <h4 style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid #e5e7eb' }}>Kapanış Bilgileri</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                  <DetailItem label="Sonuç Tarihi" value={formatDate(selectedDOF.sonuc)} />
                  <DetailItem label="DÖF'ü Kapatan" value={selectedDOF.dofKapatanınAdi} />
                  <DetailItem label="Kapanış Durumu" value={getDurumBadge(selectedDOF.kapanisDurumu)} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DetailItem: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 500 }}>{value || '-'}</div>
  </div>
);

export default DuzenleyiciOnleyiciFaaliyetYonetim;
